import { useEffect, useMemo, useState } from "react";
import { api, money, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Package, Users, Car, Wrench, MoreHorizontal, Flag, DollarSign } from "lucide-react";

const TIPO_LABEL = { material: "Material", ajudante: "Ajudante", deslocamento: "Deslocamento", equipamento: "Equipamento", outros: "Outros" };
const TIPO_ICON = { material: Package, ajudante: Users, deslocamento: Car, equipamento: Wrench, outros: MoreHorizontal };

export default function Relatorios() {
  const hoje = new Date();
  const primeiroDiaAno = new Date(hoje.getFullYear(), 0, 1).toISOString().slice(0, 10);
  const [inicio, setInicio] = useState(primeiroDiaAno);
  const [fim, setFim] = useState(hoje.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = async () => {
    setCarregando(true);
    try {
      const params = {};
      if (inicio) params.inicio = inicio;
      if (fim) params.fim = fim + "T23:59:59";
      const { data } = await api.get("/relatorios/lucros", { params });
      setData(data);
    } catch { toast.error("Erro ao carregar relatório"); }
    finally { setCarregando(false); }
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  const resumo = data?.resumo || { receita_total: 0, custo_total: 0, lucro_total: 0, margem_media: 0, patrimonio_total: 0, orcamentos_count: 0 };
  const porTipo = data?.por_tipo || {};
  const detalhado = data?.detalhado || [];
  const totalPorTipo = Object.values(porTipo).reduce((s, v) => s + Number(v || 0), 0);

  return (
    <div className="space-y-6" data-testid="relatorios-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Financeiro</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Relatórios de lucro</h1>
        <p className="text-sm text-zinc-600 mt-1">Compare receita, custos e margem por período.</p>
      </div>

      {/* Filtros */}
      <div className="bbh-card p-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1 min-w-0">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">De</label>
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} data-testid="rel-inicio" className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm" />
        </div>
        <div className="flex-1 min-w-0">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Até</label>
          <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} data-testid="rel-fim" className="mt-1 w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm" />
        </div>
        <button onClick={carregar} data-testid="rel-filtrar" className="px-4 py-2 rounded-md bbh-btn-primary text-sm font-semibold">Aplicar</button>
      </div>

      {/* Resumo */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="rel-resumo">
        <ResumoCard icon={DollarSign} label="Receita" valor={money(resumo.receita_total)} tone="indigo" sub={`${resumo.orcamentos_count} orçamento(s)`} />
        <ResumoCard icon={TrendingUp} label="Custos" valor={money(resumo.custo_total)} tone="rose" />
        <ResumoCard icon={TrendingUp} label="Lucro" valor={money(resumo.lucro_total)} tone={resumo.lucro_total >= 0 ? "emerald" : "rose"} sub={`${resumo.margem_media.toFixed(1)}% margem`} />
        <ResumoCard icon={Flag} label="Patrimônio" valor={money(resumo.patrimonio_total)} tone="violet" sub="Investimentos" />
      </section>

      {/* Por tipo */}
      <section className="bbh-card p-5" data-testid="rel-por-tipo">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">Custos por tipo</div>
        {totalPorTipo === 0 ? (
          <div className="text-sm text-zinc-500 py-4 text-center">Nenhum gasto registrado no período.</div>
        ) : (
          <div className="space-y-2">
            {Object.entries(porTipo).map(([k, v]) => {
              const Icon = TIPO_ICON[k] || MoreHorizontal;
              const pct = totalPorTipo ? (v / totalPorTipo) * 100 : 0;
              return (
                <div key={k} className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
                      <Icon className="w-3.5 h-3.5 text-zinc-500" /> {TIPO_LABEL[k]}
                    </span>
                    <span className="text-sm font-mono">{money(v)} <span className="text-xs text-zinc-400">({pct.toFixed(1)}%)</span></span>
                  </div>
                  <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#14243D]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Detalhado */}
      <section className="bbh-card p-5" data-testid="rel-detalhado">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">
          Orçamentos no período ({detalhado.length})
        </div>
        {carregando ? (
          <div className="text-sm text-zinc-500 text-center py-6">Carregando…</div>
        ) : detalhado.length === 0 ? (
          <div className="text-sm text-zinc-500 text-center py-6">Nenhum orçamento aprovado ou finalizado neste período.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-semibold uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                  <th className="py-2 pr-3">Nº</th>
                  <th className="py-2 pr-3">Cliente</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Receita</th>
                  <th className="py-2 pr-3 text-right">Custos</th>
                  <th className="py-2 pr-3 text-right">Lucro</th>
                  <th className="py-2 pr-3 text-right">Margem</th>
                </tr>
              </thead>
              <tbody>
                {detalhado.map((d) => (
                  <tr key={d.orcamento_id} className="border-b border-zinc-100" data-testid={`rel-row-${d.numero_orcamento}`}>
                    <td className="py-2 pr-3 font-mono text-xs">#{d.numero_orcamento}</td>
                    <td className="py-2 pr-3">{d.cliente_nome}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${d.status === "finalizado" ? "bg-indigo-100 text-indigo-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">{money(d.receita)}</td>
                    <td className="py-2 pr-3 text-right font-mono text-rose-600">{money(d.custos)}</td>
                    <td className={`py-2 pr-3 text-right font-mono font-bold ${d.lucro >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{money(d.lucro)}</td>
                    <td className={`py-2 pr-3 text-right font-mono text-xs ${d.margem >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{d.margem.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ResumoCard({ icon: Icon, label, valor, tone, sub }) {
  const tones = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
    indigo: "text-indigo-700 bg-indigo-50 border-indigo-200",
    violet: "text-violet-700 bg-violet-50 border-violet-200",
  };
  return (
    <div className={`rounded-md border p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-widest opacity-70">{label}</span>
        <Icon className="w-4 h-4 opacity-50" />
      </div>
      <div className="font-heading text-2xl font-bold mt-2">{valor}</div>
      {sub && <div className="text-[10px] mt-1 opacity-70">{sub}</div>}
    </div>
  );
}
